/**
 * Sorts document lines so extended warranties always appear immediately after
 * their parent product. Handles two identification strategies:
 *
 *  1. WarrantyProductId is set → matched to parent by ProductId.
 *  2. WarrantyProductId is null, IsService is truthy, and Reference is set →
 *     matched to parent by Reference === parent.ProductName (fallback for ODV
 *     lines where the backend did not populate WarrantyProductId).
 *
 * Non-warranty lines preserve their original relative order.
 * Orphaned warranties (parent not found) are appended at the end.
 */
export const sortLinesWarrantyAfterParent = lines => {
  if (!Array.isArray(lines) || lines.length === 0) return lines

  const getProductId = item => item.ProductId ?? item.productId

  const identifiedWarranties = lines.filter(l => l.WarrantyProductId != null)
  const referenceWarranties = lines.filter(
    l =>
      l.WarrantyProductId == null &&
      (l.IsExtendedWarranty || (l.IsService && l.Reference))
  )
  const referenceWarrantySet = new Set(referenceWarranties)
  const baseLines = lines.filter(
    l => l.WarrantyProductId == null && !referenceWarrantySet.has(l)
  )

  const result = []

  baseLines.forEach(line => {
    result.push(line)
    const productId = getProductId(line)
    identifiedWarranties
      .filter(w => w.WarrantyProductId === productId)
      .forEach(w => result.push(w))
    if (line.ProductName) {
      referenceWarranties
        .filter(w => w.Reference === line.ProductName && !result.includes(w))
        .forEach(w => result.push(w))
    }
  })

  const placed = new Set(result)
  identifiedWarranties.forEach(w => {
    if (!placed.has(w)) result.push(w)
  })
  referenceWarranties.forEach(w => {
    if (!placed.has(w)) result.push(w)
  })

  return result
}
